/**
 * Casing strings and per-string metres on the DEVICE, for the field ticket
 * editor in a dead zone (api#830).
 *
 * `/sync/pull` carries two additive lists beside the rows they belong to:
 *
 *   jobCasingStrings          job_casing_strings for exactly the jobs on the page
 *   ticketCasingStringMetres  ticket_casing_string_metres for exactly the tickets
 *
 * "Exactly the rows on the page" is what makes folding them safe: a job on the
 * page with no strings in the list HAS none, so it is written `[]`, a fact. A
 * job NOT on the page is left alone. And a response with no such key at all —
 * an API that predates the list — touches nothing, because an absent list is
 * "not told", never "none": writing `[]` there would tell the ticket editor a
 * ticket has no recorded metres when the device simply was not told (see the
 * sentinel note in lib/ticket-string-metres.ts).
 *
 * The folded shapes match what the online reads already put in these stores:
 * `casingStrings` (snake_case members) on the job, as GET /employee/my-jobs/:id
 * sends it, and `casingStringMetres` (camelCase) on the ticket, as
 * /employee/my-tickets sends it — so one reader serves both.
 */
import { getDB } from './db';
import type { JobCasingString } from '../../types/casing-string';

type Db = Awaited<ReturnType<typeof getDB>>;

function groupBy(rows: any[], key: string): Map<string, any[]> {
  const out = new Map<string, any[]>();
  for (const r of rows) {
    const k = r?.[key];
    if (typeof k !== 'string') continue;
    const list = out.get(k);
    if (list) list.push(r); else out.set(k, [r]);
  }
  return out;
}

/** Fold one pull page's string lists onto the job and ticket rows just stored. */
export async function foldCasingStringLists(db: Db, page: any): Promise<void> {
  const stringList = page?.jobCasingStrings?.data;
  if (Array.isArray(stringList)) {
    const byJob = groupBy(stringList, 'job_id');
    for (const job of page?.jobs?.data ?? []) {
      if (!job?.id || job.deleted_at) continue;
      const stored = await db.get('jobs', job.id);
      if (!stored) continue;
      const strings = (byJob.get(job.id) ?? [])
        .map(({ job_id, ...s }: any) => s)
        .sort((a: any, b: any) => Number(a.string_number) - Number(b.string_number));
      await db.put('jobs', { ...stored, casingStrings: strings });
    }
  }

  const metresList = page?.ticketCasingStringMetres?.data;
  if (Array.isArray(metresList)) {
    const byTicket = groupBy(metresList, 'ticket_id');
    for (const ticket of page?.tickets?.data ?? []) {
      if (!ticket?.id || ticket.deleted_at) continue;
      const stored = await db.get('tickets', ticket.id);
      if (!stored) continue;
      const metres = (byTicket.get(ticket.id) ?? []).map((r: any) => ({
        casingStringId: r.casing_string_id,
        stringNumber: Number(r.string_number),
        metres: Number(r.metres),
      }));
      await db.put('tickets', { ...stored, casingStringMetres: metres });
    }
  }
}

/**
 * The job's strings as the device last saw them, or `null` when it holds none
 * for that job — "not known", which the editor renders as no per-string inputs
 * rather than as a job with no strings.
 */
export async function readCachedJobCasingStrings(jobId: string): Promise<JobCasingString[] | null> {
  if (!jobId) return null;
  try {
    const db = await getDB();
    const job = await db.get('jobs', jobId);
    return Array.isArray(job?.casingStrings) ? job.casingStrings : null;
  } catch {
    // A sealed or unopenable cache is "not known", not an error to show.
    return null;
  }
}

/**
 * Keep the strings from an online job read on the device's copy of the job, so
 * the same ticket opened later in a dead zone still has its inputs. Only onto a
 * job row already cached — a partial job record would be a row the job list
 * reads as real. Best-effort, like every cache write here.
 */
export async function cacheJobCasingStrings(jobId: string, strings: JobCasingString[]): Promise<void> {
  if (!jobId || !Array.isArray(strings)) return;
  try {
    const db = await getDB();
    const stored = await db.get('jobs', jobId);
    if (!stored) return;
    await db.put('jobs', { ...stored, casingStrings: strings });
  } catch {
    /* best-effort */
  }
}
